import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const linkClass = 'text-[13px] font-medium tracking-wide text-[var(--vyera-text-primary)] hover:text-[var(--vyera-burgundy)] transition-colors';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { totalCount, openCart } = useCart();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const openCartAndClose = () => {
    openCart();
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
        isScrolled ? 'bg-[var(--vyera-cream)]/90 backdrop-blur-sm' : 'bg-transparent'
      }`}
    >
      <div className="pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-[max(0.75rem,env(safe-area-inset-top))] pb-3 md:pt-4 md:pb-4 sm:px-[4vw] flex justify-between items-center">
        <Link
          to="/"
          className="text-[15px] sm:text-[16px] font-light tracking-[0.25em] text-[var(--vyera-text-primary)] hover:opacity-70 transition-opacity min-h-[44px] min-w-[44px] flex items-center"
          onClick={() => setMobileOpen(false)}
        >
          VYERA
        </Link>

        <nav className="hidden md:flex gap-10">
          <Link to="/shop" className={linkClass}>Shop</Link>
          {location.pathname === '/' && (
            <>
              <a href="#story" className={linkClass}>Story</a>
              <a href="#contact" className={linkClass}>Contact</a>
            </>
          )}
          {location.pathname === '/shop' && (
            <Link to="/" className={linkClass}>Home</Link>
          )}
          <button
            type="button"
            onClick={openCart}
            className="relative p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--vyera-text-primary)] hover:text-[var(--vyera-burgundy)] transition-colors"
            aria-label={`Open cart, ${totalCount} items`}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
            {totalCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[var(--vyera-burgundy)] text-[var(--vyera-cream)] text-[10px] font-medium rounded-full">
                {totalCount > 99 ? '99+' : totalCount}
              </span>
            )}
          </button>
        </nav>

        <div className="flex items-center gap-1 md:hidden">
          <button
            type="button"
            onClick={openCartAndClose}
            className="relative p-3 min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--vyera-text-primary)]"
            aria-label={`Cart, ${totalCount} items`}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
            {totalCount > 0 && (
              <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] flex items-center justify-center bg-[var(--vyera-burgundy)] text-[var(--vyera-cream)] text-[10px] font-medium rounded-full">
                {totalCount > 99 ? '99+' : totalCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            className="p-3 min-h-[44px] min-w-[44px] flex flex-col items-center justify-center gap-1.5 text-[var(--vyera-text-primary)]"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            <span className={`w-5 h-px bg-current transition-transform ${mobileOpen ? 'rotate-45 translate-y-1' : ''}`} />
            <span className={`w-5 h-px bg-current transition-transform ${mobileOpen ? '-rotate-45 -translate-y-1' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-0 top-[calc(3.5rem+env(safe-area-inset-top))] md:hidden bg-[var(--vyera-cream)] z-[-1] transition-opacity duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden
      />

      {/* Mobile menu panel */}
      <nav
        className={`fixed top-[calc(3.5rem+env(safe-area-inset-top))] left-0 right-0 bottom-0 md:hidden bg-[var(--vyera-cream)] pt-6 pb-[env(safe-area-inset-bottom)] overflow-y-auto transition-transform duration-300 ease-out ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-label="Main navigation"
      >
        <div className="px-6 flex flex-col">
          <Link
            to="/shop"
            className="vyera-heading-md text-[var(--vyera-text-primary)] py-4 min-h-[48px] flex items-center border-b border-[var(--vyera-warm-grey)]/20"
            onClick={() => setMobileOpen(false)}
          >
            Shop
          </Link>
          {location.pathname === '/' && (
            <>
              <a
                href="#story"
                className="vyera-heading-md text-[var(--vyera-text-primary)] py-4 min-h-[48px] flex items-center border-b border-[var(--vyera-warm-grey)]/20"
                onClick={() => setMobileOpen(false)}
              >
                Story
              </a>
              <a
                href="#contact"
                className="vyera-heading-md text-[var(--vyera-text-primary)] py-4 min-h-[48px] flex items-center border-b border-[var(--vyera-warm-grey)]/20"
                onClick={() => setMobileOpen(false)}
              >
                Contact
              </a>
            </>
          )}
          {location.pathname === '/shop' && (
            <Link
              to="/"
              className="vyera-heading-md text-[var(--vyera-text-primary)] py-4 min-h-[48px] flex items-center border-b border-[var(--vyera-warm-grey)]/20"
              onClick={() => setMobileOpen(false)}
            >
              Home
            </Link>
          )}
        </div>
      </nav>
    </header>
  );
}
